import * as ToastPrim from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle, X, type LucideIcon } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '../utils/cn.js';

interface ToastItem {
  id: number;
  title?: string;
  /** Optional — either `title` or `message` must be present; UI shows whichever is set. */
  message?: string;
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'loading';
  undo?: () => void | Promise<void>;
  durationMs?: number;
}

const TONE_ICON: Partial<Record<NonNullable<ToastItem['tone']>, LucideIcon>> = {
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  danger: XCircle,
  loading: Loader2,
};

interface ToastCtx {
  show: (t: Omit<ToastItem, 'id'>) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

/**
 * Kollez rule: 8-second undo toast replaces "Are you sure?" for reversible actions.
 * `show({ undo })` renders an inline Undo button.
 */
export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useToast must be used inside <Toaster>');
  }
  return ctx;
}

export function Toaster({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const [seq, setSeq] = useState(0);

  const show = useCallback<ToastCtx['show']>(
    (t) => {
      setSeq((s) => s + 1);
      const id = seq + 1;
      setItems((prev) => [...prev, { id, durationMs: t.undo ? 8000 : 4000, ...t }]);
    },
    [seq],
  );

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={api}>
      <ToastPrim.Provider swipeDirection="right">
        {children}
        {items.map((t) => {
          const Glyph = t.tone ? TONE_ICON[t.tone] : undefined;
          return (
          <ToastPrim.Root
            key={t.id}
            className={cn('toast', t.tone && `toast-${t.tone}`)}
            duration={t.tone === 'loading' ? Infinity : t.durationMs}
            onOpenChange={(o) => {
              if (!o) dismiss(t.id);
            }}
          >
            {Glyph && (
              <Glyph
                size={16}
                aria-hidden
                className={cn('toast-icon', t.tone === 'loading' && 'toast-icon-spin')}
              />
            )}
            <div className="toast-body">
              {t.title ? <ToastPrim.Title className="toast-title">{t.title}</ToastPrim.Title> : null}
              <ToastPrim.Description className="toast-msg">{t.message}</ToastPrim.Description>
            </div>
            {t.undo ? (
              <ToastPrim.Action asChild altText="Undo">
                <button
                  type="button"
                  className="toast-undo"
                  onClick={async () => {
                    await t.undo!();
                    dismiss(t.id);
                  }}
                >
                  Undo
                </button>
              </ToastPrim.Action>
            ) : null}
            <ToastPrim.Close className="toast-close" aria-label="Close">
              <X size={12} aria-hidden />
            </ToastPrim.Close>
          </ToastPrim.Root>
          );
        })}
        <ToastPrim.Viewport className="toast-viewport" />
      </ToastPrim.Provider>
    </Ctx.Provider>
  );
}
